import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import { toFormData } from '../api/formData';
import { IconEdit, IconTrash } from '../components/Icons';
import MultilingualField from '../components/MultilingualField';
import { useNotify, errorMessage } from '../components/Toast';
import '../components/AdminUI.css';

// Single source of truth for what the storefront understands.
// Backend ProductController reads only `type` and `genre` from filter_query.
const TYPE_OPTIONS = [
  { value: '', label: 'Tous les types' },
  { value: 'optical', label: 'Optique' },
  { value: 'sunglass', label: 'Lunettes de soleil' },
  { value: 'uv', label: 'Protection UV' },
  { value: 'lens', label: 'Verres / lentilles' },
  { value: 'accessory', label: 'Accessoire' },
];

const GENRE_OPTIONS = [
  { value: '', label: 'Tous les genres' },
  { value: 'male', label: 'Homme' },
  { value: 'female', label: 'Femme' },
  { value: 'child', label: 'Enfant' },
  { value: 'mixed', label: 'Mixte' },
];

const labelFor = (options, val) => options.find((o) => o.value === val)?.label || null;

function describeFilter(fq) {
  if (!fq) return null;
  const parts = [];
  const t = labelFor(TYPE_OPTIONS, fq.type);
  const g = labelFor(GENRE_OPTIONS, fq.genre);
  if (t && fq.type) parts.push(t);
  if (g && fq.genre) parts.push(g);
  return parts.length ? parts.join(' · ') : null;
}

const blankCategory = () => ({
  slug: '',
  name: '',
  i18n: {},
  filter_query: { type: '', genre: '' },
  image: '',
  image_file: null,
  sort_order: 0,
  is_active: true,
});

export default function Categories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const notify = useNotify();

  const load = () => axios.get('/categories').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    // Skip the existing `image` URL string when the user picked a new file —
    // backend overwrites it from `image_file` anyway, and sending an empty
    // string would trip the validator.
    const payload = { ...editing };
    if (payload.image_file) delete payload.image;
    if (!payload.image_file) delete payload.image_file;

    const fd = toFormData(payload);
    try {
      if (editing.id) await axios.post(`/categories/${editing.id}`, fd);
      else await axios.post('/categories', fd);
      notify.success(editing.id ? 'Catégorie mise à jour' : 'Catégorie créée');
      setEditing(null);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Erreur lors de l'enregistrement de la catégorie"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`/categories/${id}`);
      notify.success('Catégorie supprimée');
      load();
    } catch (err) {
      notify.error(errorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  const updateFilter = (key, val) => {
    const fq = { ...(editing.filter_query || {}) };
    if (val) fq[key] = val; else delete fq[key];
    setEditing({ ...editing, filter_query: fq });
  };

  return (
    <div className="admin-page">
      {!editing && (
        <div className="admin-toolbar" style={{ justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-gold" onClick={() => setEditing(blankCategory())}>
            + Nouvelle catégorie
          </button>
        </div>
      )}

      {editing && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editing.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-form-section">
              <div className="admin-form-section-title">Identité</div>
              <div className="admin-grid admin-grid-2">
                <div>
                  <label className="admin-label">Identifiant URL (slug)</label>
                  <input
                    className="admin-input"
                    placeholder="ex. lunettes-femme"
                    value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  />
                </div>
                <div>
                  <label className="admin-label">Nom (français)</label>
                  <input
                    className="admin-input"
                    placeholder="ex. Lunettes femme"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="admin-form-section">
              <div className="admin-form-section-title">Quels produits afficher</div>
              <p className="admin-help">
                Choisissez le type de produit et/ou le genre. Laissez sur « Tous » pour ne pas filtrer.
              </p>
              <div className="admin-grid admin-grid-2">
                <div>
                  <label className="admin-label">Type de produit</label>
                  <select
                    className="admin-select"
                    value={editing.filter_query?.type || ''}
                    onChange={(e) => updateFilter('type', e.target.value)}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="admin-label">Genre</label>
                  <select
                    className="admin-select"
                    value={editing.filter_query?.genre || ''}
                    onChange={(e) => updateFilter('genre', e.target.value)}
                  >
                    {GENRE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="admin-form-section">
              <div className="admin-form-section-title">Image & affichage</div>
              <div className="admin-grid admin-grid-2">
                <div>
                  <label className="admin-label">Téléverser une image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditing({ ...editing, image_file: e.target.files?.[0] || null })}
                  />
                  {(editing.image || editing.image_file) && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={editing.image_file ? URL.createObjectURL(editing.image_file) : assetUrl(editing.image)}
                        alt=""
                        style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="admin-label">Ordre d'affichage</label>
                  <input
                    className="admin-input"
                    type="number"
                    value={editing.sort_order}
                    onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || 0, 10) })}
                  />
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13.5, color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={!!editing.is_active}
                      onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                    />
                    Catégorie active (visible sur le site)
                  </label>
                </div>
              </div>
            </div>

            <MultilingualField
              label="Traductions"
              value={editing.i18n}
              fields={['name']}
              onChange={(v) => setEditing({ ...editing, i18n: v })}
            />
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditing(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={save}>Enregistrer</button>
          </div>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflow: 'auto' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Slug</th>
              <th>Nom</th>
              <th>Filtre</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const desc = describeFilter(c.filter_query);
              return (
                <tr key={c.id}>
                  <td>
                    {c.image && (
                      <img
                        src={assetUrl(c.image)}
                        alt=""
                        style={{ width: 50, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      />
                    )}
                  </td>
                  <td>{c.slug}</td>
                  <td>{c.name}</td>
                  <td>
                    {desc ? (
                      <span style={{ fontSize: 12.5 }}>{desc}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun filtre</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${c.is_active ? 'active' : 'inactive'}`}>
                      {c.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => setEditing({ ...c, image_file: null })}>
                        <IconEdit />
                      </button>
                      <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => remove(c.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
